import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createSelector } from 'reselect';
import { push } from 'connected-react-router';
import { frontloadConnect } from 'react-frontload';
import { fetchPromotionalBannerProduct } from 'modules/search';
import { searchPromotionalBannerProductSelector } from 'modules/search/selectors';
import { pageProductAggsTopicSlugSelector } from 'modules/router/selectors';
import {
  productAggPageContentSelector,
  contentLoadingSelector,
  skipContentFrontloadRequestSelector,
} from 'modules/content/selectors';
import { topicsSelector, topicsHashSelector, topicsSlugIdMapSelector } from 'modules/topics/selectors';
import { productTypesOrganicSortedSelector, productTypesHashSelector } from 'modules/productTypes/selectors';
import { getProductAggsPageContent, resetSkipContentFrontloadRequest } from 'modules/content/actions';
import {
  PageProductAggregationOwnProps,
  PageProductAggregationProps,
  PageProductAggregation,
} from 'components/pages/PageProductAggregation/PageProductAggregation';
import { getPath, emptyArray, emptyObject } from 'utils';
import { Routes } from 'constants/index';
import { Product } from 'mxp-schemas';
import { appInitializedSelector } from 'modules/app/selectors';
import { store } from '../store';

const mapActionCreators = (
  dispatch: Dispatch,
  { isAggByTopicPage, topicSlug, productTypeSlug }: PageProductAggregationOwnProps
) => ({
  async getProductAggsPageContent(): Promise<void> {
    if (productTypeSlug === Product.ProductSlug.COURSE) {
      const productSlug = 'cpexpress';
      await dispatch(fetchPromotionalBannerProduct(productSlug));
    }
    await dispatch(getProductAggsPageContent({ isAggByTopicPage, topicSlug, productTypeSlug })).catch(() =>
      dispatch(push(getPath(Routes.NOT_FOUND)))
    );
  },
  resetSkipContentFrontloadRequest(): void {
    dispatch(resetSkipContentFrontloadRequest());
  },
});

const mapStateToProps = (initState: State.Root) => {
  const topicsHash = topicsHashSelector(initState);
  const topicsSlugIdMap = topicsSlugIdMapSelector(initState);
  const productTypesHash = productTypesHashSelector(initState);

  const subtopicsFromParamsSelector = createSelector(
    [pageProductAggsTopicSlugSelector],
    (slug: string | null): State.Subtopic[] => {
      if (!slug || !topicsHash || !topicsSlugIdMap) return emptyArray;
      const topicId: string = topicsSlugIdMap[slug];
      if (!topicId) return emptyArray;
      const subtopics: State.SubtopicsHash = topicsHash[topicId].subtopics;
      return Object.values(subtopics || emptyObject);
    }
  );

  return (state: State.Root, { topicSlug, productTypeSlug }: PageProductAggregationOwnProps) => {
    const topicId: string = topicsSlugIdMap[topicSlug || ''] || '';
    const topic = topicsHash?.[topicId];
    const topicName = topic?.name || '';
    const productTypeName = productTypesHash?.[productTypeSlug || '']?.name || '';

    return {
      topicName,
      productTypeName,
      topics: topicsSelector(state),
      subtopics: subtopicsFromParamsSelector(state),
      promotionalBannerProduct: searchPromotionalBannerProductSelector(state),
      productAggPageContent: productAggPageContentSelector(state),
      productTypesOrganicSorted: productTypesOrganicSortedSelector(state) || emptyArray,
      productTypesHash,
      topicsHash,
      topicsSlugIdMap,
      loading: contentLoadingSelector(state),
      skipContentFrontloadRequest: skipContentFrontloadRequestSelector(state),
      appInitialized: appInitializedSelector(state),
    };
  };
};

const frontload = async (props: PageProductAggregationProps) => {
  const state = store?.getState();
  const appInitialized = appInitializedSelector(state);
  if (appInitialized) {
    if (props.skipContentFrontloadRequest) return props.resetSkipContentFrontloadRequest();
    if (props.topicSlug || props.productTypeSlug) return props.getProductAggsPageContent();
  }
};

const options = {
  onUpdate: true,
  _experimental_updateFunc: (prevProps: PageProductAggregationProps, newProps: PageProductAggregationProps) =>
    prevProps.isAuth !== newProps.isAuth ||
    prevProps.appInitialized !== newProps.appInitialized ||
    prevProps.topicSlug !== newProps.topicSlug ||
    prevProps.productTypeSlug !== newProps.productTypeSlug,
};

export const PageProductAggregationContainer = connect(
  mapStateToProps,
  mapActionCreators
)(frontloadConnect(frontload, options)(PageProductAggregation));
